import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import useFetch from "../../useFetch";
import EmptyState from "../Common/EmptyState";
import "../Candidates/Candidates.css";

const getPages = (currentPage, lastPage) => {
  if (lastPage <= 5) return Array.from({ length: lastPage }, (_, index) => index + 1);
  const start = Math.max(Math.min(currentPage - 2, lastPage - 4), 1);
  return Array.from({ length: 5 }, (_, index) => start + index);
};

const initials = (name) => String(name || "C").split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word.charAt(0)).join("").toUpperCase();

const PublicCompanies = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    keyword: searchParams.get("keyword") ?? "",
    location: searchParams.get("location") ?? "",
    industry: searchParams.get("industry") ?? "",
  });
  const page = Math.max(Number(searchParams.get("page") ?? 1), 1);

  useEffect(() => {
    setFilters({
      keyword: searchParams.get("keyword") ?? "",
      location: searchParams.get("location") ?? "",
      industry: searchParams.get("industry") ?? "",
    });
  }, [searchParams]);

  const apiUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set("per_page", "9");
    params.set("page", page);
    if (searchParams.get("keyword")) params.set("keyword", searchParams.get("keyword"));
    if (searchParams.get("location")) params.set("location", searchParams.get("location"));
    if (searchParams.get("industry")) params.set("industry", searchParams.get("industry"));

    return process.env.REACT_APP_BASE_API_URL + "/companies?" + params.toString();
  }, [page, searchParams]);

  const { response, isLoading, error } = useFetch(apiUrl);
  const companies = response?.companies?.data ?? [];
  const meta = response?.companies?.meta ?? {};
  const visiblePages = getPages(meta.current_page ?? page, meta.last_page ?? 1);

  const updateUrl = (nextFilters, nextPage = 1) => {
    const params = new URLSearchParams();
    if (nextFilters.keyword) params.set("keyword", nextFilters.keyword);
    if (nextFilters.location) params.set("location", nextFilters.location);
    if (nextFilters.industry) params.set("industry", nextFilters.industry);
    if (nextPage > 1) params.set("page", nextPage);
    setSearchParams(params);
  };

  const submitFilters = (e) => {
    e.preventDefault();
    updateUrl(filters, 1);
  };

  const clearFilters = () => {
    setFilters({ keyword: "", location: "", industry: "" });
    setSearchParams({});
  };

  const changePage = (nextPage) => {
    const safePage = Math.min(Math.max(nextPage, 1), meta.last_page ?? 1);
    updateUrl(filters, safePage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <section className="page-title">
        <div className="auto-container">
          <div className="title-outer">
            <h1>Companies</h1>
            <ul className="page-breadcrumb">
              <li><Link to="/">Home</Link></li>
              <li>Companies</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="ls-section public-candidate-directory">
        <div className="auto-container">
          <form className="public-candidate-search" onSubmit={submitFilters}>
            <div className="form-group">
              <label>What</label>
              <input type="text" placeholder="Company, industry, or keyword" value={filters.keyword} onChange={(e) => setFilters((current) => ({ ...current, keyword: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Where</label>
              <input type="text" placeholder="City or remote" value={filters.location} onChange={(e) => setFilters((current) => ({ ...current, location: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Industry</label>
              <input type="text" placeholder="Industry" value={filters.industry} onChange={(e) => setFilters((current) => ({ ...current, industry: e.target.value }))} />
            </div>
            <button type="submit" className="theme-btn btn-style-one">Search</button>
            <button type="button" className="theme-btn btn-style-three" onClick={clearFilters}>Clear</button>
          </form>

          {error && <div className="alert alert-danger"><strong>{error}</strong></div>}
          {isLoading && <h5 className="text-center my-4">Loading Data</h5>}
          {!error && !isLoading && <div className="public-candidate-count">Showing {companies.length} of {meta.total ?? 0} companies</div>}

          <div className="row">
            {!error && !isLoading && companies.map((company) => (
              <div className="col-lg-4 col-md-6 col-sm-12" key={company.id}>
                <div className="public-candidate-card public-company-card">
                  <div className="public-candidate-avatar">
                    {company.logo_url ? <img src={company.logo_url} alt={company.company_name} /> : <span>{initials(company.company_name)}</span>}
                  </div>
                  <h4>{company.company_name}</h4>
                  <p>{company.industry || "Company"}</p>
                  <ul>
                    <li><span className="la la-map-marker"></span>{company.location || "Location not added"}</li>
                    <li><span className="la la-briefcase"></span>{company.open_jobs_count ?? 0} open jobs</li>
                  </ul>
                  <div className="candidate-skill-tags">
                    {company.company_size && <span>{company.company_size}</span>}
                    {company.founded_year && <span>Founded {company.founded_year}</span>}
                  </div>
                  <Link to={`/companies/${company.id}`} className="theme-btn btn-style-three">View Company</Link>
                </div>
              </div>
            ))}
            {!error && !isLoading && companies.length === 0 && (
              <div className="col-12">
                <EmptyState
                  icon="la la-building"
                  title="No companies found"
                  text="Try broadening your search terms or location."
                  actionText="Reset Search"
                  actionTo="/companies"
                />
              </div>
            )}
          </div>

          {!error && !isLoading && (meta.last_page ?? 1) > 1 && (
            <nav className="ls-pagination" aria-label="Company pagination">
              <ul>
                <li className="prev"><button type="button" disabled={(meta.current_page ?? page) === 1} onClick={() => changePage((meta.current_page ?? page) - 1)}><i className="fa fa-arrow-left"></i></button></li>
                {visiblePages.map((pageNumber) => (
                  <li key={pageNumber}><button type="button" className={pageNumber === (meta.current_page ?? page) ? "current-page" : ""} onClick={() => changePage(pageNumber)}>{pageNumber}</button></li>
                ))}
                <li className="next"><button type="button" disabled={(meta.current_page ?? page) === meta.last_page} onClick={() => changePage((meta.current_page ?? page) + 1)}><i className="fa fa-arrow-right"></i></button></li>
              </ul>
            </nav>
          )}
        </div>
      </section>
    </>
  );
};

export default PublicCompanies;
